'use client'

import useNotificationMenu from '@/app/core/components/notification/hooks/useNotificationMenu'
import NotificationMenu from '@/app/core/components/notification/components/NotificationMenu'
import NotificationContainer from '@/app/core/components/notification/components/NotificationContainer'
import NotificationaUserAvatars from '@/app/core/components/notification/NotificationUserAvatars'
import NotificationResolvedUserName from '@/app/core/components/notification/NotificationResolvedUserName'
import {
  isNotificationUnread,
  isUserAssignedCustomNotification,
  isUserUnassignedCustomNotification,
} from '@/app/core/components/notification/utils'
import { useResolveRooms } from '@/dc-shared-web/src'
import { InboxNotificationCustomData } from '@liveblocks/core'

const getNotificationContent = (
  assignmentType: 'playbook' | 'checklist',
  checklistName: string,
  sectionName: string | undefined,
  isUnassigned: boolean
) => {
  let target

  if (assignmentType === 'playbook') {
    if (sectionName) {
      target = (
        <>
          {' the playbook section '}
          <span className='tw-font-medium'>{sectionName}</span>
        </>
      )
    } else {
      target = (
        <>
          {' the playbook '}
          <span className='tw-font-medium'>{checklistName}</span>
        </>
      )
    }
  } else {
    if (sectionName) {
      target = (
        <>
          {' the checklist section '}
          <span className='tw-font-medium'>{sectionName}</span>
        </>
      )
    } else {
      target = (
        <>
          {' the checklist '}
          <span className='tw-font-medium'>{checklistName}</span>
        </>
      )
    }
  }

  return {
    action: isUnassigned ? 'unassigned you from' : 'assigned you to',
    target,
  }
}

// Extract data specific to assignment notifications
export const useAssignmentNotificationDetails = (
  notification:
    | InboxNotificationCustomData<'$userAssigned'>
    | InboxNotificationCustomData<'$userUnassigned'>
) => {
  const activityData = notification.activities[0].data

  // Format the notification URL
  const notificationUrl = `/crunches/${activityData.crunchUuid}?infoPanel=${
    activityData.assignmentType === 'checklist' ? 'Checklist' : 'Cheatsheet'
  }${activityData.assignmentType === 'checklist' ? `&checklist=${activityData.checklistUuid}` : ''}${
    activityData.sectionUuid ? `&section=${activityData.sectionUuid}` : ''
  }`

  const roomId = notification.roomId || ''
  // Use the useResolveRooms hook to get room information efficently
  const { data: roomsData, isLoading: isLoadingRooms } = useResolveRooms([
    roomId,
  ])

  return {
    assignmentType: activityData.assignmentType,
    sectionUuid: activityData.sectionUuid,
    sectionName: activityData.sectionName,
    notificationUrl,
    triggerUser: isUserAssignedCustomNotification(notification)
      ? notification.activities[0].data.assignedBy
      : notification.activities[0].data.unassignedBy,
    roomId,
    roomInfo: roomsData && roomsData[0],
    isLoadingRoomInfo: isLoadingRooms,
  }
}

const NotificationContent = ({
  assignmentType,
  triggerUser,
  crunchName,
  checklistName,
  sectionName,
  isUnassigned,
}: {
  assignmentType: 'checklist' | 'playbook'
  triggerUser: string
  crunchName: string
  checklistName: string
  sectionName: string | undefined
  isUnassigned: boolean
}) => {
  const { action, target } = getNotificationContent(
    assignmentType,
    checklistName,
    sectionName,
    isUnassigned
  )

  return (
    <div>
      <NotificationResolvedUserName
        userId={triggerUser}
        className='tw-font-medium'
      />{' '}
      {action} {target}
      {' in '}
      <span className='tw-font-medium'>{crunchName}</span>
    </div>
  )
}

export default function AssignmentNotification({
  notification,
  onClick,
}: {
  notification:
    | InboxNotificationCustomData<'$userAssigned'>
    | InboxNotificationCustomData<'$userUnassigned'>
  onClick: () => void
}) {
  const {
    assignmentType,
    notificationUrl,
    triggerUser,
    isLoadingRoomInfo,
    sectionName,
    roomInfo,
  } = useAssignmentNotificationDetails(notification)

  const isUnread = isNotificationUnread(notification)

  const {
    isHovered,
    isMenuOpened,
    setIsMenuOpened,
    onMarkAsRead,
    onDelete,
    onMouseEnter,
    onMouseLeave,
  } = useNotificationMenu(notification.id)

  return (
    <NotificationContainer
      isUnread={isUnread}
      notificationUrl={notificationUrl}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <NotificationaUserAvatars involvedUsers={[triggerUser]} />
      <div
        className='tw-flex tw-flex-col tw-gap-2 tw-grow'
        data-testid={`notification-item`}
      >
        <div className='tw-flex tw-gap-2'>
          <div className='tw-break-inner tw-text-sm tw-grow'>
            {isLoadingRoomInfo || !roomInfo ? (
              <div className='tw-font-medium tw-h-6 tw-animate-skeleton-shimmer' />
            ) : (
              <NotificationContent
                assignmentType={assignmentType}
                triggerUser={triggerUser}
                crunchName={roomInfo.crunchName}
                checklistName={roomInfo.checklistName}
                sectionName={sectionName}
                isUnassigned={isUserUnassignedCustomNotification(notification)}
              />
            )}
          </div>
          <NotificationMenu
            notificationId={notification.id}
            isUnread={isUnread}
            isHovered={isHovered}
            isMenuOpened={isMenuOpened}
            setIsMenuOpened={setIsMenuOpened}
            date={notification.notifiedAt}
            onMarkAsRead={onMarkAsRead}
            onDelete={onDelete}
          />
        </div>
      </div>
    </NotificationContainer>
  )
}
